import { Component, NgZone } from '@angular/core';
import { NavController, Platform } from '@ionic/angular';
import { SplashScreen } from '@capacitor/splash-screen';
import { AvatarService } from './services/avatar.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Auth } from '@angular/fire/auth';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Preferences } from '@capacitor/preferences';
import { OnesignalService } from './services/one-signal.service';
import { App } from '@capacitor/app';
import { AlertController } from '@ionic/angular';

import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  currentLanguage: string;
  user: any;
  source: string;
  loading: boolean = true;

  constructor(
    private platform: Platform,
    private ngZone: NgZone,
    public avatar: AvatarService,
    private auth: Auth,
    private nav: NavController,
    private router: Router,
    private translate: TranslateService,
    private oneSignalService: OnesignalService,
    private alertController: AlertController
  ) {
    this.initializeApp();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        console.log('Navigation ended:', event);
      }
    });
  }

  async initializeApp() {
    this.injectGetProjectButton();
    // Set default language
    this.currentLanguage = this.translate.getDefaultLang() || 'en';
    await this.initializeTranslation();
    this.initialize();
    this.setupNotificationHandler();
    this.initializeBackButtonCustomHandler();
  }

  injectGetProjectButton() {
    if (!(environment as any).defaultLogin?.projectButton || !(environment as any).projectUrl) return;
    
    const buttonId = 'get-full-project-btn';
    if (document.getElementById(buttonId)) return;

    const button = document.createElement('a');
    button.id = buttonId;
    button.href = (environment as any).projectUrl;
    button.target = '_blank';
    button.innerHTML = `
      <img src="assets/icon/favicon.png" style="width: 24px; height: 24px;" onerror="this.src='https://pegasus-superapp-monorepo.vercel.app/favicon.ico'">
      <span>Get full project</span>
    `;

    const style = document.createElement('style');
    style.innerHTML = `
      #${buttonId} {
        position: fixed;
        bottom: 85px;
        right: 20px;
        background: linear-gradient(135deg, #FFD700, #FFA500);
        color: #000;
        padding: 12px 20px;
        border-radius: 50px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.3);
        z-index: 10000;
        text-decoration: none;
        font-weight: 800;
        display: flex;
        align-items: center;
        gap: 10px;
        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        animation: float-btn 3s ease-in-out infinite;
        border: 2px solid #fff;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      #${buttonId}:hover {
        transform: scale(1.1) translateY(-5px);
        box-shadow: 0 15px 35px rgba(0,0,0,0.4);
        background: linear-gradient(135deg, #FFA500, #FFD700);
      }
      #${buttonId}:active {
        transform: scale(0.95);
      }
      @keyframes float-btn {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      @media (max-width: 768px) {
        #${buttonId} span { display: none; }
        #${buttonId} { padding: 12px; border-radius: 50%; bottom: 80px; }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(button);
  }

  initializeBackButtonCustomHandler() {
    this.platform.backButton.subscribeWithPriority(0, () => {
      // Only show the exit confirmation if we are on root pages
      const currentUrl = this.router.url;
      if (currentUrl === '/home' || currentUrl === '/login' || currentUrl === '/') {
        this.showExitConfirmation();
      } else {
        // If not on a root page, let the default back navigation handle it
        this.nav.back();
      }
    });
  }

  async showExitConfirmation() {
    const alert = await this.alertController.create({
      header: 'Exit App',
      message: 'Are you sure you want to exit the app?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Yes',
          handler: () => {
            App.exitApp();
          }
        }
      ]
    });
    await alert.present();
  }

  /**
   * Set up notification click handler for shared ride deep links
   * Uses the existing OnesignalService
   */
  private setupNotificationHandler(): void {
    // Notification handling is done via OnesignalService
    // The service already sets up click listeners in OneSignalInit()
    // Deep link handling for shared rides is managed in home.page.ts via query params
    console.log('Notification handler delegated to OnesignalService');
  }

  async initialize() {
    this.platform.ready().then(async (readySource: string) => {
      this.source = readySource;
      this.auth.onAuthStateChanged(async (user) => {
        this.user = user;
        console.log('Auth state changed:', user);

        if (user) {
          // User is signed in
          console.log('User is signed in:', user);
        } else {
          // User is signed out
          console.log('User is signed out');
          this.ngZone.run(() => {
            this.nav.navigateRoot('/login');
          });
        }

        if (readySource != 'dom') {
          // statusBar.setBackgroundColor is often problematic with overlay.
          // Setting Style.Default allows the system to manage its color based on theme.
          await StatusBar.setStyle({ style: Style.Default });
        }
        await this.LoadSplash();
        this.loading = false;
      });
    });
  }

  async LoadSplash() {
    await SplashScreen.show();

    if (this.source != 'dom')
      await StatusBar.setOverlaysWebView({ overlay: true });
  }

  gotoProfile() {
    this.nav.navigateForward('profile');
  }

  async initializeTranslation() {
    this.translate.setDefaultLang('en'); // Set English as default

    try {
      const { value } = await Preferences.get({ key: 'user-lang' });
      const lang = value || 'en'; // Default to English
      this.translate.use(lang);
      this.currentLanguage = lang;
    } catch (error) {
      console.error('Error loading language preference:', error);
      this.translate.use('en');
      this.currentLanguage = 'en';
    }
  }

  async changeLanguage(lang: string) {
    this.currentLanguage = lang;
    this.translate.use(lang);
    await Preferences.set({ key: 'user-lang', value: lang });
  }
}
